/* eslint-disable max-lines */

import { extractByTypename } from '@local/utils'

import {
  CancelledBookingHistory,
  CancelledOrderHistory,
  CancelledTrainTicketsConnection,
  TimelinesPayload,
  BookingResult,
  OrderResult,
  UpcomingTrainTicketsConnection,
  TimelineEdge,
} from '@web/federation/gql/graphql'
import unionBy from '@web/libs/lodash/unionBy'
import {
  CancelledTrainTicketEdgeKeySpecifier,
  StrictTypedTypePolicies as FederationTypePolicies,
  TimelineEdgeKeySpecifier,
} from '@web/pages/v5/libs/apollo/typePolicies.helper'

const federationTypePolicies: FederationTypePolicies = {
  Query: {
    fields: {
      timelines: {
        keyArgs: ['input', '@connection'],
        merge: (existing: TimelinesPayload | undefined, incoming: TimelinesPayload) => {
          if (!existing) return incoming

          const { TimelinesSucceed: existingTimelinesSucceed } = extractByTypename(existing)
          const { TimelinesSucceed: incomingTimelinesSucceed } = extractByTypename(incoming)
          if (!existingTimelinesSucceed) return incoming

          if (!incomingTimelinesSucceed) return existing

          return { ...existing, ...incoming }
        },
      },
      me: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          return { ...existing, ...incoming }
        },
      },
    },
  },
  TimelineEdge: {
    keyFields: ['timeline', ['id' as unknown as TimelineEdgeKeySpecifier]], // use timeline id as keyFields
  },
  TimelinesSucceed: {
    fields: {
      edges: {
        merge: (existing, incoming: TimelineEdge[]): TimelineEdge[] => {
          if (!existing) return incoming

          if (!incoming) return existing

          return unionBy(existing, incoming, '__ref')
        },
      },
      pageInfo: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming) return existing

          return { ...existing, ...incoming }
        },
      },
    },
  },
  PlaceSummary: {
    merge: true,
    keyFields: ['id'],
    fields: {
      category: {
        merge: true,
      },
      directionLandingUrl: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          return { ...existing, ...incoming }
        },
      },
      address: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          return { ...existing, ...incoming }
        },
      },
    },
  },
  User: {
    merge: true,
    fields: {
      profile: {
        merge: true,
      },
      stat: {
        merge: true,
      },
    },
  },
  CancelledTrainTicketEdge: {
    keyFields: ['node', ['id' as unknown as CancelledTrainTicketEdgeKeySpecifier]],
  },
  MeSucceed: {
    merge: true,
    fields: {
      cancelledBookingHistory: {
        keyArgs: false,
        merge: (existing, incoming: CancelledBookingHistory) => {
          if (!existing) return incoming

          if (!incoming) return existing

          return {
            ...incoming,
            bookings: unionBy(existing.bookings, incoming.bookings, '__ref'),
          }
        },
      },
      cancelledOrderHistory: {
        keyArgs: false,
        merge: (existing, incoming: CancelledOrderHistory) => {
          if (!existing) return incoming

          if (!incoming) return existing

          return {
            ...incoming,
            orders: unionBy(existing.orders, incoming.orders, '__ref'),
          }
        },
      },
      cancelledTrainTickets: {
        keyArgs: false,
        merge: (existing, incoming: CancelledTrainTicketsConnection): CancelledTrainTicketsConnection => {
          if (!existing) return incoming

          if (!incoming) return existing

          return {
            ...incoming,
            edges: unionBy(existing.edges, incoming.edges, '__ref'),
          }
        },
      },
      upcomingBookings: {
        keyArgs: false,
        merge: (existing: BookingResult, incoming: BookingResult): BookingResult => {
          if (!existing?.bookings) return incoming

          if (!incoming?.bookings) return existing

          return {
            ...incoming,
            bookings: unionBy(existing.bookings as any, incoming.bookings, '__ref'),
          }
        },
      },
      upcomingOrders: {
        keyArgs: false,
        merge: (existing: OrderResult, incoming: OrderResult): OrderResult => {
          if (!existing?.orders) return incoming

          if (!incoming?.orders) return existing

          return {
            ...incoming,
            orders: unionBy(existing.orders as any, incoming.orders, '__ref'),
          }
        },
      },
      upcomingTrainTickets: {
        keyArgs: false,
        merge: (
          existing: UpcomingTrainTicketsConnection,
          incoming: UpcomingTrainTicketsConnection,
        ): UpcomingTrainTicketsConnection => {
          if (!existing) return incoming

          if (!incoming) return existing

          return {
            ...incoming,
            edges: unionBy(existing.edges as any, incoming.edges, 'cursor'),
          }
        },
      },
    },
  },
}

export default federationTypePolicies
